import React from "react";

/**
 * A SMALL, SAFE MARKDOWN RENDERER for announcements (2026-10-09).
 *
 * Builds React elements directly - no HTML strings, no innerHTML - so
 * nothing an author pastes can inject markup or script. Supports what the
 * editor offers: `#`/`##`/`###` headings (shown as section labels),
 * paragraphs, `-`/`*` bullets, `1.` numbered lists, **bold**, *italic* /
 * _italic_, and [links](https://... or /in-app/path). Anything else is
 * shown as plain text.
 */

type Block =
  | { kind: "heading"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

export function parseBlocks(md: string): Block[] {
  const lines = String(md ?? "").replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: "p", text: para.join(" ") });
    para = [];
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    const h = /^#{1,3}\s+(.*)$/.exec(t);
    const ul = /^[-*]\s+(.*)$/.exec(t);
    const ol = /^\d+[.)]\s+(.*)$/.exec(t);
    if (h) {
      flush();
      blocks.push({ kind: "heading", text: h[1] });
    } else if (ul) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "ul") last.items.push(ul[1]);
      else blocks.push({ kind: "ul", items: [ul[1]] });
    } else if (ol) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "ol") last.items.push(ol[1]);
      else blocks.push({ kind: "ol", items: [ol[1]] });
    } else if (/^\s{2,}\S/.test(raw) && blocks.length && (blocks[blocks.length - 1].kind === "ul" || blocks[blocks.length - 1].kind === "ol") && !para.length) {
      // Continuation line of the previous list item.
      const last = blocks[blocks.length - 1] as { items: string[] };
      last.items[last.items.length - 1] += " " + t;
    } else {
      para.push(t);
    }
  }
  flush();
  return blocks;
}

const safeHref = (url: string): string | null => {
  const u = url.trim();
  if (/^https?:\/\/\S+$/i.test(u)) return u;
  if (/^\/[^/\s]\S*$/.test(u) || u === "/") return u;
  return null;
};

/** Inline: [text](url), **bold**, *italic* / _italic_. */
export function renderInline(text: string, onLink?: (href: string) => void, keyBase = "i"): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${n++}`;
    if (m[1] !== undefined) {
      const href = safeHref(m[2]);
      if (!href) out.push(m[1]);
      else if (href.startsWith("/"))
        out.push(
          <a
            key={key}
            href={href}
            onClick={(e) => {
              if (onLink) {
                e.preventDefault();
                onLink(href);
              }
            }}
          >
            {m[1]}
          </a>,
        );
      else
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {m[1]}
          </a>,
        );
    } else if (m[3] !== undefined) out.push(<b key={key}>{renderInline(m[3], onLink, key)}</b>);
    else out.push(<i key={key}>{m[4] ?? m[5]}</i>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export const AnnouncementBody: React.FC<{ md: string; fontSize?: number; onLink?: (href: string) => void }> = ({ md, fontSize = 14, onLink }) => {
  const blocks = parseBlocks(md);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === "heading")
          return (
            <div key={i} style={{ fontSize: Math.max(11, fontSize - 3), fontWeight: 600, letterSpacing: "0.6px", textTransform: "uppercase", color: "#6B6B6B", margin: i === 0 ? "2px 0 6px" : "14px 0 6px" }}>
              {renderInline(b.text, onLink, `h${i}`)}
            </div>
          );
        if (b.kind === "ul" || b.kind === "ol") {
          const Tag = b.kind;
          return (
            <Tag key={i} style={{ margin: "0 0 10px 0", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, onLink, `l${i}-${j}`)}</li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={i} style={{ margin: "0 0 10px 0" }}>
            {renderInline(b.text, onLink, `p${i}`)}
          </p>
        );
      })}
    </>
  );
};

/** Words and minutes, for the editor's reading-time line. */
export function readingTime(md: string): { words: number; minutes: number } {
  const words = String(md ?? "").replace(/[#*_[\]()>`-]/g, " ").split(/\s+/).filter(Boolean).length;
  return { words, minutes: Math.max(1, Math.round(words / 200)) };
}
